import { useCallback, memo, useState, useEffect } from 'react'
import { GS } from 'back-end-api'

import { hp } from 'styles/Sizes'
import DefaultBtn from 'components/common/buttons/Default'
import Separator from 'components/common/Separator'
import Typography from 'components/common/Typography'
import { useTranslation } from 'services/Translation'

import ReactCodeInput from 'react-code-input'
import { Container, inputStyle, inputStyleInvalid } from './Styles'
import texts from './Texts'

const OTP_SIZE = 5

const OTPForm = ({ username, password, onStepChange, onPasswordError }) => {
  const { t } = useTranslation(texts)
  const [otp, setOtp] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendMessage, setResendMessage] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)

  // Cooldown timer effect
  useEffect(() => {
    let timer
    if (resendCooldown > 0) {
      timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
    }
    return () => clearTimeout(timer)
  }, [resendCooldown])

  const handleOtpChange = useCallback(
    value => {
      if (value.length === OTP_SIZE) setOtp(value)
      else if (otp) setOtp('')
      // Clear any previous messages when user starts typing
      if (resendMessage) setResendMessage('')
      if (error) setError('')
    },
    [otp, resendMessage, error]
  )

  const handleResendOtp = useCallback(async () => {
    if (resendCooldown > 0) return

    setResendLoading(true)
    setResendMessage('')
    setError('')

    try {
      const res = await GS.sendOneTimePassword({
        destination: username,
      })

      if (res?.error) {
        setResendMessage(t('resendError'))
      } else {
        setResendMessage(t('resendSuccess'))
        setResendCooldown(30) // 30 second cooldown
      }
    } catch (err) {
      setResendMessage(t('resendError'))
    }

    setResendLoading(false)
  }, [username, t, resendCooldown])

  const handleSubmit = useCallback(async () => {
    setLoading(true)
    const res = await GS.resetPassword({
      one_time_password_destination: username,
      one_time_password: parseInt(otp, 10),
      new_password: password,
    })
    if (res?.error) {
      switch (res.error?.status) {
        case 404:
          setError(t('otpExpired'))
          break
        case 500:
          onPasswordError(t('passwordAlreadyUsed'))
          break
        default:
          setError(t('error'))
      }
    } else {
      setError('')
      onStepChange()
    }
    setLoading(false)
  }, [onPasswordError, onStepChange, otp, password, t, username])

  const getResendButtonText = () => {
    if (resendCooldown > 0) {
      return t('resendCooldown').replace('{seconds}', resendCooldown)
    }
    return t('resendBtn')
  }

  return (
    <Container>
      <ReactCodeInput
        type="number"
        fields={OTP_SIZE}
        inputStyle={inputStyle}
        inputStyleInvalid={inputStyleInvalid}
        name="OTP"
        onChange={handleOtpChange}
        isValid={!error}
      />
      <Separator size={hp(1)} />
      <Typography text={error} hidden={!error} error center />
      <Typography
        text={resendMessage}
        hidden={!resendMessage}
        center
        style={{
          color: resendMessage === t('resendSuccess') ? '#4CAF50' : '#f44336',
          fontSize: '14px',
          fontWeight: 'bold',
          marginTop: '10px',
        }}
      />

      <Separator size={hp(2)} />

      {/* Resend OTP Section */}
      <div style={{ width: '100%', textAlign: 'center', marginBottom: '10px' }}>
        <Typography text={t('didntReceive')} center style={{ fontSize: '12px', color: '#666' }} />
      </div>

      <div style={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px' }}>
        <button
          type="button"
          disabled={resendCooldown > 0 || resendLoading}
          onClick={handleResendOtp}
          style={{
            width: '60%',
            backgroundColor: resendCooldown > 0 || resendLoading ? '#ccc' : '#1C165B',
            color: 'white',
            border: '1px solid #1C165B',
            borderRadius: '4px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: resendCooldown > 0 || resendLoading ? 'not-allowed' : 'pointer',
            opacity: resendCooldown > 0 || resendLoading ? 0.6 : 1,
            transition: 'all 0.3s ease',
          }}
        >
          {resendLoading ? t('sending') : getResendButtonText()}
        </button>
      </div>

      <Separator size={hp(3)} />
      <DefaultBtn
        disabled={!otp || loading}
        label={t('btnLabel')}
        withNext
        onClick={handleSubmit}
        reversedColors
        style={{ width: '50%', margin: 'auto' }}
      />
    </Container>
  )
}

OTPForm.whyDidYouRender = true

export default memo(OTPForm)
